import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Film, Check, Sparkles, Zap, TrendingUp, Users } from "lucide-react";

const SeedanceVideo = () => {
  const features = [
    { icon: Film, title: "Cinematic Movie Generation", description: "Powered by Seedance 2.0 — generate full cinematic scenes, ads, and short films from a single prompt or storyboard." },
    { icon: Sparkles, title: "Hollywood-Grade Visuals", description: "Realistic motion, lighting, and physics. Ideal for product launches, brand films, and high-impact social content." },
    { icon: Zap, title: "Fast Iteration", description: "Generate, refine, and re-render in minutes — not weeks. Test multiple creative directions before going live." },
    { icon: TrendingUp, title: "Platform-Optimized", description: "Delivered in every aspect ratio for TikTok, Reels, Shorts, YouTube, OTT, and paid ads — ready to publish." },
  ];

  const benefits = [
    "Replace expensive shoots with on-demand AI cinema",
    "Unlimited creative iterations every month",
    "Brand-consistent visuals across every campaign",
    "Faster launches, lower production cost",
  ];

  const includes = [
    "Up to 30 Seedance 2.0 generated videos per month",
    "Custom storyboards, scripts, and direction by Neil AI creative team",
    "Brand kit integration (logos, colors, voice, characters)",
    "Multi-platform delivery (9:16, 16:9, 1:1, 4:5)",
    "Unlimited revisions until approved",
  ];

  const perfectFor = [
    "Brands launching products and need cinematic ads fast",
    "Agencies scaling creative output without scaling headcount",
    "DTC and ecommerce teams running constant ad creative tests",
    "Founders building thought-leadership and brand films",
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Seedance 2.0 Movie & Video Generator — Neil AI Global Solutions"
        description="Cinematic AI video and movie generation powered by Seedance 2.0. Done-for-you ads, short films, and brand content at scale."
        keywords="Seedance 2.0, AI movie generator, AI video generator, cinematic AI video, ad creative, brand films"
      />
      <Navigation />

      <main className="pt-40 pb-16">
        <section className="container mx-auto px-4 py-16 text-center">
          <Badge className="mb-4 text-lg px-6 py-2 bg-gradient-to-r from-primary to-secondary">
            Seedance 2.0 Movie & Video Generator
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-primary via-accent to-secondary bg-clip-text text-transparent">
            Cinematic AI Video. On Demand.
          </h1>
          <p className="text-2xl text-foreground font-semibold mb-4">Up to 30 Cinematic Videos Per Month</p>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">
            Neil AI's creative team uses Seedance 2.0 to produce Hollywood-grade ads, brand films, and short-form content for your business — no cameras, no crews, no waiting.
          </p>
          <div className="flex gap-4 justify-center flex-wrap mb-8">
            <Button size="lg" className="text-lg" asChild>
              <a href="https://link.fastpaydirect.com/payment-link/68938782ddc6a61a45c5d90a">Start Your Plan</a>
            </Button>
            <Button size="lg" variant="outline" className="text-lg" asChild>
              <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics">Schedule a Demo</a>
            </Button>
          </div>
        </section>

        <section className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">What We Create for You</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => {
              const Icon = feature.icon;
              return (
                <Card key={i} className="border-2 border-primary/20 hover:border-primary/40 transition-colors">
                  <CardHeader>
                    <div className="h-14 w-14 rounded-lg bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center mb-4 mx-auto">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>
                    <CardTitle className="text-lg text-center">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground text-center">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </section>

        <section className="container mx-auto px-4 py-16 bg-muted/30 rounded-2xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center">The Value for Your Business</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {benefits.map((benefit, i) => (
                <Card key={i} className="border-2 border-primary/20">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <p className="text-foreground font-medium">{benefit}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="container mx-auto px-4 py-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">Who It's Perfect For</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {perfectFor.map((item, i) => (
                <Card key={i} className="border-2 border-primary/20 hover:border-primary/40 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Users className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <p className="text-foreground">{item}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="container mx-auto px-4 py-16 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">Your Monthly Plan Includes</h2>
            <div className="grid gap-4">
              {includes.map((item, i) => (
                <Card key={i} className="border-2 border-primary/20">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-3">
                      <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <p className="text-foreground font-medium text-lg">{item}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="container mx-auto px-4 py-16">
          <Card className="max-w-4xl mx-auto text-center border-4 border-primary/30 bg-gradient-to-br from-primary/5 to-secondary/5">
            <CardHeader>
              <CardTitle className="text-3xl md:text-4xl font-bold mb-4">
                Launch Cinematic Campaigns This Week
              </CardTitle>
              <p className="text-xl text-muted-foreground">
                We direct, generate, and deliver. You publish and grow. Get your first Seedance 2.0 videos within days.
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex gap-4 justify-center flex-wrap">
                <Button size="lg" className="text-lg" asChild>
                  <a href="https://link.fastpaydirect.com/payment-link/68938782ddc6a61a45c5d90a">Get Started</a>
                </Button>
                <Button size="lg" variant="secondary" className="text-lg" asChild>
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics">Schedule Free Demo</a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SeedanceVideo;
